import { keepPreviousData } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import type { ReactNode } from "react";
import {
  Badge,
  Button,
  Card,
  FilterToggle,
  LicensePlate,
  Pagination,
  Skeleton,
  TBody,
  THead,
  Table,
  Td,
  Th,
  Tr,
} from "@rumbledirect/ui-v2";
import type {
  ChargingContract,
  ChargingContractStatus,
} from "@/charging/charging";
import { ChargingSubjectsCell } from "@/charging/ChargingSubjectsCell";
import {
  contractStatusLabel,
  contractStatusOptions,
  contractTypeLabel,
  formatTariffPerKwh,
} from "@/charging/chargingLabels";
import type { ChargingContractStatusCounts } from "@/charging/useChargingContractStatusCounts";
import { useChargingContractStatusCounts } from "@/charging/useChargingContractStatusCounts";
import { useGetAdminChargingContracts } from "@/lib/api/generated/charging/charging";

const ITEMS_PER_PAGE = 15;
const COLUMN_COUNT = 7;
const SKELETON_ROW_KEYS = Array.from(
  { length: 5 },
  (_, index) => `charging-contract-skeleton-${index}`,
);

const STATUS_VALUES = new Set<string>(
  contractStatusOptions.map((option) => option.value),
);

interface ChargingContractsSearch {
  readonly page: number;
  readonly status?: ChargingContractStatus;
}

export const Route = createFileRoute("/_authenticated/charging-contracts")({
  validateSearch: (
    search: Record<string, unknown>,
  ): ChargingContractsSearch => {
    const page = Number(search.page);
    const status = String(search.status ?? "");
    return {
      page: Number.isInteger(page) && page >= 1 ? page : 1,
      ...(STATUS_VALUES.has(status)
        ? { status: status as ChargingContractStatus }
        : {}),
    };
  },
  component: ChargingContractsPage,
});

function ChargingContractsPage(): ReactNode {
  const { page, status } = Route.useSearch();
  const navigate = Route.useNavigate();

  const { data, isPending, isPlaceholderData, isError, refetch } =
    useGetAdminChargingContracts(
      {
        page,
        itemsPerPage: ITEMS_PER_PAGE,
        ...(status ? { status } : {}),
      },
      { query: { placeholderData: keepPreviousData } },
    );

  const { data: statusCounts } = useChargingContractStatusCounts();

  const totalItems = data?.totalItems ?? 0;

  const setStatusFilter = (next?: ChargingContractStatus): void => {
    void navigate({
      search: () => ({
        page: 1,
        ...(next ? { status: next } : {}),
      }),
    });
  };

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          Laadcontracten
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Alle laadcontracten met de medewerkers en voertuigen die er op dit
          moment op laden.
        </p>
      </div>

      <fieldset className="flex flex-wrap items-center gap-2 rounded-2xl border border-border/70 bg-surface p-4 shadow-xs">
        <legend className="sr-only">Filter laadcontracten</legend>
        <FilterToggle
          active={status === undefined}
          onClick={() => setStatusFilter(undefined)}
        >
          Alle
          <ContractStatusCount counts={statusCounts} />
        </FilterToggle>
        {contractStatusOptions.map(({ value, label }) => (
          <FilterToggle
            key={value}
            active={status === value}
            onClick={() => setStatusFilter(value)}
          >
            {label}
            <ContractStatusCount counts={statusCounts} status={value} />
          </FilterToggle>
        ))}
      </fieldset>

      <Card className="overflow-hidden">
        <Table>
          <THead>
            <Tr>
              <Th>Aanbieder</Th>
              <Th className="w-36">Soort</Th>
              <Th className="w-28">Status</Th>
              <Th className="w-44">Kenteken</Th>
              <Th className="w-52">Medewerker</Th>
              <Th className="hidden w-40 lg:table-cell">Tarief</Th>
              <Th className="w-12">
                <span className="sr-only">Openen</span>
              </Th>
            </Tr>
          </THead>
          <TBody
            aria-busy={isPlaceholderData}
            className={isPlaceholderData ? "opacity-60" : undefined}
          >
            <ChargingContractRows
              isPending={isPending}
              isError={isError}
              members={data?.member}
              onRetry={() => void refetch()}
            />
          </TBody>
        </Table>
      </Card>

      {totalItems > ITEMS_PER_PAGE && (
        <Pagination
          page={page}
          pageSize={ITEMS_PER_PAGE}
          totalItems={totalItems}
          status={isPending ? "pending" : isError ? "error" : "ready"}
          onPageChange={(next) =>
            void navigate({ search: (prev) => ({ ...prev, page: next }) })
          }
        />
      )}
    </div>
  );
}

/**
 * Count badge on a filter chip, read from the single per-status counts request
 * so the chips keep showing their own totals regardless of the active filter.
 * Without `status` it shows the total across every status.
 */
function ContractStatusCount({
  counts,
  status,
}: {
  readonly counts: ChargingContractStatusCounts | undefined;
  readonly status?: ChargingContractStatus;
}): ReactNode {
  if (counts === undefined) {
    return <span className="text-xs tabular-nums opacity-70">–</span>;
  }

  const count =
    status === undefined
      ? Object.values(counts).reduce((total, value) => total + value, 0)
      : (counts[status] ?? 0);

  return <span className="text-xs tabular-nums opacity-70">{count}</span>;
}

function ChargingContractRows({
  isPending,
  isError,
  members,
  onRetry,
}: {
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly members: readonly ChargingContract[] | undefined;
  readonly onRetry: () => void;
}): ReactNode {
  if (isPending) {
    return SKELETON_ROW_KEYS.map((key) => (
      <Tr key={key}>
        <Td>
          <Skeleton className="h-4 w-40" />
        </Td>
        <Td>
          <Skeleton className="h-4 w-24" />
        </Td>
        <Td>
          <Skeleton className="h-5 w-16 rounded-full" />
        </Td>
        <Td>
          <Skeleton className="h-6 w-28" />
        </Td>
        <Td>
          <Skeleton className="h-4 w-32" />
        </Td>
        <Td className="hidden lg:table-cell">
          <Skeleton className="h-4 w-24" />
        </Td>
        <Td />
      </Tr>
    ));
  }

  if (isError) {
    return (
      <Tr>
        <Td colSpan={COLUMN_COUNT} className="py-10 text-center">
          <div className="space-y-3" role="alert">
            <p className="text-muted-foreground">
              We konden de laadcontracten niet laden.
            </p>
            <Button variant="outline" size="sm" onClick={onRetry}>
              Opnieuw proberen
            </Button>
          </div>
        </Td>
      </Tr>
    );
  }

  if (members === undefined || members.length === 0) {
    return (
      <Tr>
        <Td
          colSpan={COLUMN_COUNT}
          className="py-10 text-center text-muted-foreground"
        >
          Nog geen laadcontracten.
        </Td>
      </Tr>
    );
  }

  return members.map((contract) => {
    const subjects = contract.activeSubjects;
    return (
      <Tr key={contract.id}>
        <Td className="font-medium">{contract.providerName}</Td>
        <Td>{contractTypeLabel(contract.contractType)}</Td>
        <Td>
          <ContractStatusBadge status={contract.status} />
        </Td>
        <Td>
          <ChargingSubjectsCell
            label={subjects.licensePlate}
            count={subjects.vehicleCount}
            contractId={contract.id}
            plural="voertuigen"
          >
            {subjects.licensePlate ? (
              <LicensePlate value={subjects.licensePlate} size="sm" />
            ) : undefined}
          </ChargingSubjectsCell>
        </Td>
        <Td>
          <ChargingSubjectsCell
            label={subjects.employeeName}
            count={subjects.employeeCount}
            contractId={contract.id}
            plural="medewerkers"
          />
        </Td>
        <Td className="hidden text-muted-foreground lg:table-cell">
          {formatTariffPerKwh(contract.tariffPerKwh)}
        </Td>
        <Td className="text-right">
          {contract.id ? (
            <Link
              to="/charging-contracts/$contractId"
              params={{ contractId: contract.id }}
              search={{ page: 1 }}
              aria-label={`Laadcontract van ${contract.providerName} openen`}
              className="inline-grid size-6 place-items-center rounded-md text-muted-foreground transition-colors hover:text-foreground"
            >
              <ChevronRight className="size-4" aria-hidden="true" />
            </Link>
          ) : null}
        </Td>
      </Tr>
    );
  });
}

function ContractStatusBadge({
  status,
}: {
  readonly status: ChargingContract["status"];
}): ReactNode {
  const variant =
    status === "active"
      ? "success"
      : status === "suspended"
        ? "warning"
        : "neutral";

  return (
    <Badge variant={variant} size="sm">
      {contractStatusLabel(status)}
    </Badge>
  );
}
